/**
 * №44 — the HOOK layer that feeds `app-commands.ts`.
 *
 * The split is the whole design: everything that decides what a command IS and
 * where it GOES is a pure function next door, so a `tsx` gate can enumerate it
 * without a browser. This file only fetches — and it fetches from the caches the
 * App Shell has already filled wherever it can.
 *
 * **Lazy by construction.** The palette mounts its index only once it has been
 * opened for the first time (`AppCommandPalette`'s `everOpened` latch, the same
 * pattern the console's palette uses), so the per-entity-type row queries below
 * cost nothing on a page load where nobody presses ⌘K.
 */
import { useMemo } from "react";
import { useQueries } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router";
import { mcpClient } from "../../../lib/mcp-client";
import { queryKeys, toolNames, displayFieldFor } from "../../../lib/query-keys";
import { useEntityTypes, useWorkspaceInfo } from "../../../lib/hooks/use-kuvio";
import { useAppSitemap } from "../../hooks/use-app-sitemap";
import { useThemedLabels } from "../../hooks/use-themed-labels";
import { useV3Theme } from "../../hooks/use-v3-theme";
import { useCoPanel } from "../../components/collab/co-panel-context";
import { SYSTEM_FALLBACK_PLACES, type SitemapEntry } from "./sitemap";
import { workbenchNavRoute } from "./workbench-registry";
import { conversationRouteFor } from "./route-anchor-matrix";
import { buildAppCommands, placeBackedEntities, type AppActionSpec, type AppCommandData, type AppRecordRow } from "./app-commands";
import type { Command } from "../../components/command-palette/types";
import type { ListEntitiesResult } from "../../../lib/types";

/** Top N rows per declared type — the console palette's own cap (S3.12). The
 *  recency boost is what surfaces the ones you actually touch; a firm big enough
 *  for this to bite needs the `Search` SURFACE, which №44 leaves out on purpose. */
const ROW_CAP = 50;

/**
 * №53 (53.0) — returns the places ALONGSIDE the commands.
 *
 * The palette's resting state groups by the server's `section` mark (D-CR22), and
 * this hook already derives the filtered Places list that `buildAppCommands`
 * consumes. Returning it is one line; re-deriving it in the palette would be a
 * second copy of the axis filter and the `SYSTEM_FALLBACK_PLACES` default, which
 * is how the rail and the palette would eventually disagree about the six.
 */
export function useAppCommands(): { commands: Command[]; places: SitemapEntry[] } {
  const navigate = useNavigate();
  const location = useLocation();
  const sitemap = useAppSitemap();
  const wsInfo = useWorkspaceInfo();
  const entityTypes = useEntityTypes();
  const labels = useThemedLabels();
  const { theme, toggle: toggleTheme } = useV3Theme();
  const rightZone = useCoPanel();

  const places = useMemo(
    () => (sitemap.data?.places ?? SYSTEM_FALLBACK_PLACES).filter((e) => e.axis === "places"),
    [sitemap.data?.places],
  );

  // Rows of every PLACE-BACKED declared type, one query each — same shape and
  // staleness as the console palette, pointed at this shell's record route.
  //
  // The Places filter is applied here as well as in `buildAppCommands`, and that
  // is not redundant: the builder's copy is the RULE (and what the gate proves),
  // this one is the COST. Without it we would fire a `list_*` per declared type —
  // options, option-set options, time entries — on first ⌘K, and then throw every
  // one of those results away.
  const surfaced = useMemo(() => placeBackedEntities(places), [places]);
  const rowTypes = useMemo(
    () => (entityTypes.data ?? []).filter((t) => !t.hide_from_synthesis && surfaced.has(t.slug)),
    [entityTypes.data, surfaced],
  );
  const rowQueries = useQueries({
    queries: rowTypes.map((t) => ({
      queryKey: [...queryKeys.entities(t.slug), "app-cmdk"] as const,
      queryFn: () => mcpClient.listEntities(toolNames(t).list),
      enabled: !!entityTypes.data,
      staleTime: 60_000,
    })),
  });

  const records = useMemo<AppRecordRow[]>(() => {
    const out: AppRecordRow[] = [];
    rowTypes.forEach((t, i) => {
      const data = rowQueries[i]?.data as ListEntitiesResult | undefined;
      const df = displayFieldFor(t);
      for (const e of (data?.entities ?? []).slice(0, ROW_CAP)) {
        const dv = e[df];
        out.push({
          entity: t.slug,
          entityLabel: t.name,
          id: e.id,
          label: typeof dv === "string" && dv.length > 0 ? dv : e.id,
        });
      }
    });
    return out;
  }, [rowTypes, rowQueries]);

  // The roster, read exactly as `ShellNav` reads it — agents in the library root.
  const roster = useMemo(
    () => (wsInfo.data?.library_root ?? []).filter((n) => n.type === "agent").map((n) => ({ id: n.id, name: n.name })),
    [wsInfo.data?.library_root],
  );

  // The workbench registry is a client-side lookup, so resolving it here keeps
  // `app-commands.ts` free of anything a node script cannot import.
  const workbenchRoutes = useMemo(() => {
    const m: Record<string, string | undefined> = {};
    for (const e of places) if (e.workbench) m[e.workbench] = workbenchNavRoute(e.workbench);
    return m;
  }, [places]);

  const actions = useMemo<AppActionSpec[]>(() => {
    const out: AppActionSpec[] = [
      {
        id: "app:action:theme",
        label: theme === "dark" ? "Switch to light" : "Switch to dark",
        icon: theme === "dark" ? "sun" : "moon",
        keywords: ["theme", "dark", "light", "appearance"],
        run: toggleTheme,
      },
      {
        id: "app:action:rail",
        label: rightZone.expanded ? "Collapse the work rail" : "Open the work rail",
        icon: "layout-sidebar-right",
        keywords: ["rail", "panel", "chat", "conversation", "collapse", "expand"],
        run: () => rightZone.setExpanded(!rightZone.expanded),
      },
    ];
    // "Open the conversation on the stage" only where the matrix says this
    // surface HAS a conversation row — the same read the stage head does, so
    // the command cannot offer a promotion this surface does not declare.
    const convo = conversationRouteFor(location.pathname);
    if (convo) {
      out.push({
        id: "app:action:conversation",
        label: "Open the conversation on the stage",
        icon: "message-2",
        subtitle: "Here",
        keywords: ["chat", "conversation", "thread", "promote", "stage"],
        run: () => navigate(convo),
      });
    }
    return out;
  }, [theme, toggleTheme, rightZone, location.pathname, navigate]);

  const data = useMemo<AppCommandData>(
    () => ({ pathname: location.pathname, places, roster, records, workbenchRoutes }),
    [location.pathname, places, roster, records, workbenchRoutes],
  );

  const commands = useMemo(
    () => buildAppCommands(data, { collective: labels.collective, navigate, actions }),
    [data, labels.collective, navigate, actions],
  );

  return useMemo(() => ({ commands, places }), [commands, places]);
}
